import LockIcon from '@mui/icons-material/Lock';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Card,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useQuery } from '@tanstack/react-query';

import { fetchPermissions, fetchRoles } from '@/api/rbac';
import type { Permission, Role } from '@/types/api';

/**
 * Read-only role and permission browser.
 *
 * Creating and editing custom roles is done in the Filament back-office,
 * which already has the grouped permission editor. Duplicating that in the
 * SPA would mean two implementations of the same escalation rules — this page
 * exists so a salesperson can SEE what a role grants without needing panel
 * access.
 */
export function RolesPage() {
  const roles = useQuery({ queryKey: ['roles'], queryFn: fetchRoles });
  const permissions = useQuery({ queryKey: ['permissions'], queryFn: fetchPermissions });

  if (roles.isPending || permissions.isPending) return <CircularProgress size={24} />;
  if (roles.error) return <Alert severity="error">You do not have access to roles.</Alert>;

  const byName = new Map<string, Permission>((permissions.data ?? []).map((p) => [p.name, p]));

  return (
    <Stack spacing={2}>
      <Stack spacing={0.5}>
        <Typography variant="h1">Roles</Typography>
        <Typography variant="body2" color="text.secondary">
          Built-in roles are shared by every organisation and cannot be changed. Custom roles are
          managed in the admin panel.
        </Typography>
      </Stack>

      <Card sx={{ p: 1 }}>
        {roles.data?.map((role: Role) => (
          <Accordion key={role.id} disableGutters elevation={0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexGrow: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {role.label}
                </Typography>
                {role.is_system && (
                  <Tooltip title="Built in — shared by every organisation">
                    <LockIcon fontSize="inherit" color="disabled" />
                  </Tooltip>
                )}
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${role.permissions?.length ?? 0} permissions`}
                />
                {role.member_count !== undefined && (
                  <Chip size="small" label={`${role.member_count} members`} />
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {role.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {role.description}
                </Typography>
              )}
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {(role.permissions ?? []).map((name) => (
                  <Tooltip key={name} title={byName.get(name)?.description ?? name}>
                    <Chip size="small" variant="outlined" label={name} />
                  </Tooltip>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Card>
    </Stack>
  );
}
